import { api } from "@shared/lib/api";
import { BackednResponse } from "@shared/lib/types";
import {
  ICampaignDeliverableTaskApprove,
  ICampaignDeliverableTaskForm,
  ICampaignDeliverableTaskUpload,
  ICreateScriptComment,
  ICreateScriptTimeframe,
  IDeleteScriptComment,
  IDeleteScriptTimeframe,
  IEditScriptTimeframe,
  IGetScriptingResponse,
  IGetTaskFormRequest,
  IGetTaskFormResponse,
  IGetUnattachedScriptingResponse,
  IReplyToScriptComment,
  IResolveScriptComment,
  IScriptingComment,
  IScriptingCommentReply,
  IUpdateScripting,
  IUploadScripting,
} from "@shared/lib/types/briefCampains";
import {
  ICreateBriefConfigTaskRequest,
  IEditBriefConfigTaskRequest,
  IGetBriefConfigDeliverableFacetValueResponse,
  IGetBriefConfigResponse,
  IRenameBriefConfigDeliverableRequest,
  IReorderBriefConfigTaskRequest,
} from "@shared/lib/types/briefConfig";
import {
  ICampaignFieldConfigResponse,
  IFieldConfig,
} from "@shared/lib/types/briefInfluencerV2";

export class BriefConfigService {
  // Brief Deliverable Config Methods
  public static async getBriefDeliverableConfig(briefId: string) {
    const url = `brief/configuration/deliverable?briefId=${briefId}`;
    const response = (await api.get(url).json()) as BackednResponse<
      IGetBriefConfigResponse[]
    >;
    return response;
  }

  public static async createBriefConfigTask(
    data: ICreateBriefConfigTaskRequest
  ) {
    const url = "brief/configuration/deliverable/task";
    await api.post(url, {
      json: {
        ...data,
      },
    });
  }

  public static async deleteBriefConfigTask(params: {
    briefId: string;
    deliverableConfigRefId: string;
    taskId: string;
  }) {
    const url = "brief/configuration/deliverable/task";
    await api.delete(url, {
      json: {
        ...params,
      },
    });
  }

  public static async editBriefConfigTask(data: IEditBriefConfigTaskRequest) {
    const url = "brief/configuration/deliverable/task";
    await api.put(url, {
      json: {
        ...data,
      },
    });
  }

  public static async reorderBriefConfigTask(
    data: IReorderBriefConfigTaskRequest
  ) {
    const url = "brief/configuration/deliverable/task/reorder";
    await api.put(url, {
      json: {
        ...data,
      },
    });
  }
  public static async renameBriefConfigDeliverable(
    data: IRenameBriefConfigDeliverableRequest
  ) {
    const url = "brief/configuration/deliverable/rename";
    await api.put(url, {
      json: {
        ...data,
      },
    });
  }
  public static async saveDeliverableConfig(data: {
    briefId: string;
    deliverableConfigRefId: string;
  }) {
    const url = "brief/configuration/deliverable/save";
    await api.put(url, {
      json: {
        ...data,
      },
    });
  }

  public static async getBriefConfigDeliverableFacets(briefId: string) {
    const url = `campaign/influencer/facets`;
    const response = (await api
      .post(url, {
        json: {
          briefId,
        },
      })
      .json()) as BackednResponse<IGetBriefConfigDeliverableFacetValueResponse>;
    return response;
  }

  public static async uploadTaskContent(data: ICampaignDeliverableTaskUpload) {
    const url = "campaign/influencer/task/upload";
    const response = (await api
      .post(url, {
        json: {
          ...data,
        },
      })
      .json()) as BackednResponse<{
      campaignTaskForm: ICampaignDeliverableTaskForm;
    }>;

    return response;
  }
  public static async approveTaskContent(
    data: ICampaignDeliverableTaskApprove
  ) {
    const url = "campaign/influencer/task/approve";
    await api.post(url, {
      json: {
        ...data,
      },
    });
  }
  public static async requestEditTaskContent(
    data: ICampaignDeliverableTaskApprove
  ) {
    const url = "campaign/influencer/task/request-edits";
    await api.post(url, {
      json: {
        ...data,
      },
    });
  }
  public static async getScripting(scriptingId: string) {
    const url = `scripting?scriptId=${scriptingId}`;
    const response = (await api
      .get(url)
      .json()) as BackednResponse<IGetScriptingResponse>;
    return response;
  }

  // Scripting Methods
  public static async updateScriptingBasic(data: IUpdateScripting) {
    const url = "scripting";
    await api.post(url, { json: { ...data } });
  }

  public static async uploadScripting(data: IUploadScripting) {
    const url = "scripting/upload";
    await api.post(url, { json: { ...data } });
  }

  public static async createScriptTimeframe(data: ICreateScriptTimeframe) {
    const url = "scripting/timeframe";
    await api.put(url, { json: { ...data } });
  }

  public static async editScriptTimeframe(data: IEditScriptTimeframe) {
    const url = "scripting/timeframe";
    await api.post(url, { json: { ...data } });
  }

  public static async deleteScriptTimeframe(data: IDeleteScriptTimeframe) {
    const url = "scripting/timeframe";
    await api.delete(url, { json: { ...data } });
  }

  public static async createScriptComment(data: ICreateScriptComment) {
    const url = "scripting/comment";
    return (await api
      .post(url, { json: { ...data } })
      .json()) as BackednResponse<{
      comment: IScriptingComment;
      clientCommentId?: string;
    }>;
  }
  public static async deleteScriptComment(data: IDeleteScriptComment) {
    const url = "scripting/comment";

    await api.delete(url, { json: { ...data } });
  }

  public static async replyToScriptComment(data: IReplyToScriptComment) {
    const url = "scripting/comment/reply";
    return (await api
      .post(url, { json: { ...data } })
      .json()) as BackednResponse<{
      reply: IScriptingCommentReply;
      clientReplyId?: string;
    }>;
  }

  public static async resolveScriptComment(data: IResolveScriptComment) {
    const url = "scripting/comment";
    return (await api
      .put(url, { json: { ...data } })
      .json()) as BackednResponse<{
      comment: IScriptingComment;
    }>;
  }

  public static async addScriptToTask(data: {
    campaignInfluencerId: string;
    deliverableConfigRefId: string;
    taskId: string;
  }) {
    const url = "campaign/influencer/task/add-script";
    await api.post(url, { json: { ...data } });
  }
  public static async updateTaskDeadline(data: {
    campaignInfluencerId: string;
    deliverableConfigRefId: string;
    taskId: string;
    deadline: string;
  }) {
    const url = "campaign/influencer/task/deadline";
    const newObject: any = { ...data };
    if (!data.deadline) {
      delete newObject.deadline;
    }

    await api.post(url, { json: { ...newObject } });
  }

  public static async getBriefCampaignDataFields(briefId: string) {
    const url = `brief/search/data-fields/v2/operations/facets?briefId=${briefId}`;
    const response = (await api
      .get(url)
      .json()) as BackednResponse<ICampaignFieldConfigResponse>;
    return response;
  }

  public static async createBriefCampaignDataFieldsV2(
    briefId: string,
    operationsCustomFieldsV2: IFieldConfig
  ) {
    const url = `brief/custom-fields/operations`;
    await api.post(url, {
      json: {
        briefId,
        operationsCustomFieldsV2,
      },
    });
  }

  public static async editCampaignDataFieldV2Data(
    campaignInfluencerId: string,
    dataField: { [key: string]: string | number }
  ) {
    const url = `campaign/influencer/dynamic-fields`;
    await api.post(url, {
      json: {
        campaignInfluencerId,
        dynamicFields: { ...dataField },
      },
    });
  }
  public static async toggleBriefCampaignDataFieldV2(
    briefId: string,
    brandId: string,
    visibileOperationsCustomFieldsV2: IFieldConfig[]
  ) {
    const url = `brief`;
    await api.patch(url, {
      json: {
        briefId,
        brandId,
        visibileOperationsCustomFieldsV2,
      },
    });
  }

  public static async getTaskForm(params: IGetTaskFormRequest) {
    const url = `campaign/influencer/task/search/form`;
    const response = (await api
      .post(url, { json: { ...params } })
      .json()) as BackednResponse<IGetTaskFormResponse>;
    return response;
  }

  public static async editTaskContent(data: ICampaignDeliverableTaskUpload) {
    const url = "campaign/influencer/task/edit/form";
    await api.post(url, {
      json: {
        campaignInfluencerId: data.campaignInfluencerId,
        deliverableConfigRefId: data.deliverableConfigRefId,
        taskId: data.taskId,
        form: { title: data?.title || "" },
      },
    });
  }
  public static async creatScript(briefId: string) {
    const url = "scripting";
    const response = (await api
      .put(url, {
        json: {
          briefId,
        },
      })
      .json()) as BackednResponse<IGetUnattachedScriptingResponse>;
    return response;
  }
}
